import { Partner, partnerService } from "./partnerService";
import { transactionService } from "./transactionService";

import axios from "axios";

interface MTL extends Partner {
  type: string;
  token: string;
  mobile: string;
  healthCardId: string;
  clientId: string;

  consentCode: string;
  refCode: string;
  // Add any additional properties specific to MTL here.
}

const partnerMTLService = {
  isActive: () => {
    return !!getPartnerObject();
  },

  onIndexMounted: (location: Location) => {
    const partner = getPartnerObject();
    if (!partner) return;

    const type = new URLSearchParams(location.search).get("type") || "MTL-TH"; // For language selection.
    const mobile = new URLSearchParams(location.search).get("mobile") || "";
    // ISO27018: do not log mobile number (PII)
    // console.log("MTL Mobile number:", mobile);
    const healthcardid =
      new URLSearchParams(location.search).get("healthcardid") || "";
    const clientId = new URLSearchParams(location.search).get("clientId") || "";

    partner.type = type;
    partner.mobile = mobile;
    partner.healthCardId = healthcardid;
    partner.clientId = clientId;

    if (partner.userData) {
      const firstName = partner.userData.firstName;
      const lastName = partner.userData.lastName;
      const hn = generateHnNumber(firstName, lastName, partner.userData.idCard);
      partner.userData.hn = hn;

      const telFromQuery = mobile?.trim();
      const currentTel = partner.userData.tel?.trim();

      partner.userData.tel = telFromQuery || currentTel || "ไม่ระบุ";
      // ISO27018: do not log phone number (PII)
      // console.log("MTL Tel number:", partner.userData.tel);
    }

    setPartnerObject(partner);
    // ISO27018: do not log full partner object containing PII
    // console.log("MTL partner initialized:", partner);

    // Add any additional methods specific to MTL here.
  },

  getConsent: async (lang: string) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BASE_URL + "/MuangthaiGetConsent",
        {
          consentLanguage: lang.toUpperCase(),
        }
      );
      if (response.data.IsSuccess) {
        const data = response.data.data;
        const partner = getPartnerObject();
        if (partner) {
          partner.consentCode = data.consentCode;
          partner.refCode = data.refCode;
          setPartnerObject(partner);
        }
        return {
          consentLongText: data.consentLongText || "",
        };
      } else {
        throw new Error("Failed to get consent");
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  },

  confirmConsent: async () => {
    try {
      const partner = getPartnerObject();
      if (!partner) return false;

      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Create JSON request with camelCase fields and transaction context
      const requestData = {
        transactionId: jwtSession.transactionId,
        customerRefId: partner.refCode,
        membershipId: "", // Add if available
        policyNo: "", // Add if available
        consentChannel: "web",
        consentData: {
          consentCode: partner.consentCode,
          refCode: partner.refCode,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_BASE_URL + "/MuangthaiConfirmConsent",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      if (response.data.IsSuccess) {
        // ISO27018: do not log transactionId
        // console.log("MTL consent confirmed:", { transactionId: jwtSession.transactionId });
        return true;
      } else {
        throw new Error("Failed to confirm consent");
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  },

  rejectConsent: async () => {
    try {
      const partner = getPartnerObject();
      if (!partner) return false;

      const jwtSession = transactionService.getJwtSession();
      if (!jwtSession) {
        throw new Error("No valid JWT session found. Please register first.");
      }

      if (!jwtSession.transactionId) {
        throw new Error("No transaction ID found. Please create queue first.");
      }

      // Create JSON request with camelCase fields
      const requestData = {
        transactionId: jwtSession.transactionId,
        reason: "User rejected consent",
        rejectData: {
          consentCode: partner.consentCode,
          refCode: partner.refCode,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_BASE_URL + "/MuangthaiRejectConsent",
        requestData,
        {
          headers: transactionService.getAuthHeaders(),
        }
      );

      if (response.data.IsSuccess) {
        // ISO27018: do not log transactionId
        // console.log("MTL consent rejected:", { transactionId: jwtSession.transactionId });
        return true;
      } else {
        throw new Error("Failed to reject consent");
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  },

  onConfirmConsent: async () => {
    const partner = getPartnerObject();
    if (!partner || !partner.userData) return false;

    const hasBenefit = await partnerMTLService.getBenefit(
      partner.healthCardId,
      partner.userData.searchOption,
      partner.refCode,
      partner.clientId
    );

    if (!hasBenefit) {
      // handle error
    } else {
      // send notification and check queue
    }
  },

  getBenefit: async (
    healthcard: string,
    searchOption: string,
    consentRefCode: string,
    clientId: string
  ) => {
    const jwtSession = transactionService.getJwtSession();
    if (!jwtSession) {
      throw new Error("No valid JWT session found. Please register first.");
    }

    if (!jwtSession.transactionId) {
      throw new Error("No transaction ID found. Please create queue first.");
    }

    // Create JSON request with camelCase fields and transaction context
    const requestData = {
      patientId: jwtSession.patientId,
      transactionId: jwtSession.transactionId,
      visitType: "consultation",
      isCheckBenefit: true,
      insuranceData: {
        listId: healthcard,
        searchOption: searchOption,
        consentRefCode: consentRefCode,
        clientId: clientId,
      },
    };

    const response = await axios.post(
      process.env.REACT_APP_BASE_URL + "/MuangthaiGateway",
      requestData,
      {
        headers: transactionService.getAuthHeaders(),
      }
    );

    if (response.data.IsSuccess === true) {
      // ISO27018: do not log transactionId
      // console.log("MTL benefit check successful:", { transactionId: jwtSession.transactionId });
      return { success: true, errorMessage: "" };
    } else {
      return { success: false, errorMessage: response.data.ErrMessage };
    }
  },
};

const getPartnerObject = () => {
  const partner = partnerService.getPartner();
  if (partner && partner.name.trim() === "MTL") {
    return partner as MTL;
  }
  return undefined;
};

const setPartnerObject = (partner: MTL) => {
  partnerService.setPartner(partner);
};

const generateHnNumber = (
  firstName: string,
  lastName: string,
  card: string
) => {
  const getHash = (input: string) => {
    let hash = 0,
      len = input.length;
    for (let i = 0; i < len; i++) {
      hash = (hash << 5) - hash + input.charCodeAt(i);
      hash |= 0; // to 32bit integer
    }
    return hash.toString();
  };

  let hnConcat = card;

  if (!hnConcat || hnConcat === "null" || hnConcat === "undefined") {
    hnConcat = getHash(firstName + " " + lastName);
  }

  if (hnConcat === "undefined") {
    hnConcat = Date.now().toString();
  }

  return "MTL-" + hnConcat;
};

export { partnerMTLService, MTL };
